import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { MaskLines } from "./Reveal";
import { ASSETS } from "@/data/content";
import { useAudio } from "@/context/AudioContext";

const Hero = () => {
  const ref = useRef(null);
  const { playTrack } = useAudio();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="overview" ref={ref} className="relative pt-32 sm:pt-40 pb-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <motion.div style={{ opacity: fade }}>
          <div className="flex items-center gap-3 mb-8">
            <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3.5 py-1.5 text-xs font-medium tracking-wide text-neutral-600">
              <span className="h-1.5 w-1.5 rounded-full bg-playing" />
              INDEPENDENT PRODUCTION HOUSE · JHB
            </span>
          </div>

          <h1 className="font-heading font-semibold tracking-tight text-black text-[13vw] leading-[0.92] sm:text-7xl lg:text-8xl">
            <MaskLines lines={["Precision", "in sound."]} />
            <span className="block text-neutral-300">
              <MaskLines lines={["Independence in motion."]} delay={0.28} />
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-10 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between"
          >
            <p className="max-w-md text-base sm:text-lg text-neutral-600 leading-relaxed">
              A Flat Records, an independent label and studio led by{" "}
              <span className="text-black font-medium">Master A Flat</span>, engineering
              modern African and global sound with uncompromising craft.
            </p>
            <div className="flex items-center gap-3">
              <button
                data-testid="hero-play"
                onClick={() => playTrack("intro")}
                className="group inline-flex items-center gap-3 rounded-full bg-black pl-2 pr-6 py-2 text-white transition-colors duration-300 hover:bg-neutral-800"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-black transition-transform duration-300 group-hover:scale-105">
                  <Play size={16} className="ml-0.5 fill-black" />
                </span>
                <span className="font-medium">Play catalog</span>
              </button>
              <a
                href="#catalog"
                data-testid="hero-explore"
                className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-6 py-3.5 font-medium text-black transition-colors duration-300 hover:bg-neutral-100"
              >
                Explore <ArrowUpRight size={16} />
              </a>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Spotlighted studio frame */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 mt-14 sm:mt-20"
      >
        <div className="afr-grain relative overflow-hidden rounded-3xl border border-gray-200 shadow-sm aspect-[16/9] sm:aspect-[21/9]">
          <motion.img
            style={{ y, scale }}
            src={ASSETS.studio}
            alt="AKA and Master A Flat in the studio"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/5 to-transparent" />
          <div className="absolute bottom-5 left-5 sm:bottom-8 sm:left-8 flex items-center gap-4 rounded-2xl border border-white/30 bg-white/15 px-4 py-3 backdrop-blur-xl">
            <div className="flex h-9 items-end gap-[3px]">
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={i}
                  className="afr-eq-bar w-1 rounded-full bg-white"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
            <div className="text-white">
              <p className="text-xs uppercase tracking-widest opacity-80">Now in session</p>
              <p className="font-heading font-medium">MASTER A FLAT</p>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
